import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Button, Space, Popconfirm } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import css from './DietModeAndTypes.module.css';
import { DietTypeCreateModal } from '../settingSectionModals/DietTypeCreateModal/DietTypeCreateModal';
import { DietTypeUpdateModal } from '../settingSectionModals/DietTypeUpdateModal/DietTypeUpdateModal';
import { DietModeCreateModal } from '../settingSectionModals/DietModeCreateModal/DietModeCreateModal';
import { DietModeUpdateModal } from '../settingSectionModals/DietModeUpdateModal/DietModeUpdateModal';
import { dietTypeService, dietModeService } from '../../../services/settings-services';
import { dietTypeActions, dietModeActions } from '../../../store/slices/settings-slices';

const DietModeAndTypes = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  
  // State for Diet Type modals
  const [isCreateTypeModalOpen, setIsCreateTypeModalOpen] = useState(false);
  const [isUpdateTypeModalOpen, setIsUpdateTypeModalOpen] = useState(false);
  const [selectedDietType, setSelectedDietType] = useState(null);
  
  // State for Diet Mode modals
  const [isCreateModeModalOpen, setIsCreateModeModalOpen] = useState(false);
  const [isUpdateModeModalOpen, setIsUpdateModeModalOpen] = useState(false);
  const [selectedDietMode, setSelectedDietMode] = useState(null);
  const [selectedDietTypeIdForMode, setSelectedDietTypeIdForMode] = useState(null);

  const { dietTypes, status: typeStatus, trigger: typeTrigger } = useSelector(store => store.dietTypes);
  const { dietModes, status: modeStatus, trigger: modeTrigger } = useSelector(store => store.dietModes);

  const currentLanguage = i18n.language;
  const loading = typeStatus === 'Loading' || modeStatus === 'Loading';

  useEffect(() => {
    dispatch(dietTypeActions.getAllDietTypes());
    dispatch(dietModeActions.getAllDietModes());
  }, [typeTrigger, modeTrigger, dispatch]);

  // Diet Type handlers
  const handleDeleteType = async (id) => {
    try {
      await dietTypeService.deleteById(id);
      dispatch(dietTypeActions.changeTrigger());
      toast.success(t('Diet type deleted successfully'));
    } catch (error) {
      console.error('Error deleting diet type:', error);
      toast.error(t('Error deleting diet type'));
    }
  };

  const handleUpdateType = (record) => {
    setSelectedDietType(record);
    setIsUpdateTypeModalOpen(true);
  };

  const handleCreateType = () => {
    setIsCreateTypeModalOpen(true);
  };

  // Diet Mode handlers
  const handleDeleteMode = async (id) => {
    try {
      await dietModeService.deleteById(id);
      dispatch(dietModeActions.changeTrigger());
      toast.success(t('Diet mode deleted successfully'));
    } catch (error) {
      console.error('Error deleting diet mode:', error);
      toast.error(t('Error deleting diet mode'));
    }
  };

  const handleUpdateMode = (record) => {
    setSelectedDietMode(record);
    setIsUpdateModeModalOpen(true);
  };

  const handleCreateMode = (dietTypeId) => {
    setSelectedDietTypeIdForMode(dietTypeId);
    setIsCreateModeModalOpen(true);
  };

  // Get diet modes for a specific diet type
  const getDietModesForType = (dietTypeId) => {
    return dietModes.filter(mode => mode.dietTypeId === dietTypeId);
  };

  // Nested table for diet modes
  const expandedRowRender = (record) => {
    const modesForType = getDietModesForType(record.id);

    const modeColumns = [
      {
        title: '#',
        dataIndex: 'id',
        key: 'id',
        width: 80,
        render: (text, record, index) => index + 1,
      },
      {
        title: t('Diet Mode Name'),
        dataIndex: 'name',
        key: 'name',
        render: (name) => name?.[currentLanguage] || name?.['en'] || '',
      },
      {
        title: t('Actions'),
        key: 'actions',
        width: 150,
        render: (_, modeRecord) => (
          <Space size="middle">
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => handleUpdateMode(modeRecord)}
              size="small"
            >
              {t('Edit')}
            </Button>
            <Popconfirm
              title={t('Are you sure you want to delete this diet mode?')}
              onConfirm={() => handleDeleteMode(modeRecord.id)}
              okText={t('Yes')}
              cancelText={t('No')}
            >
              <Button
                type="primary"
                danger
                icon={<DeleteOutlined />}
                size="small"
              >
                {t('Delete')}
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ];

    return (
      <div className={css.expandedRow}>
        <div className={css.nestedTableHeader}>
          <h4>{t('Diet Modes')}</h4>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleCreateMode(record.id)}
            size="small"
          >
            {t('Add Diet Mode')}
          </Button>
        </div>
        <Table
          columns={modeColumns}
          dataSource={modesForType}
          rowKey="id"
          pagination={false}
          className={css.nestedTable}
        />
      </div>
    );
  };

  // Main table columns for diet types
  const typeColumns = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (text, record, index) => index + 1,
    },
    {
      title: t('Icon'),
      dataIndex: 'icon',
      key: 'icon',
      width: 80,
      render: (icon) => icon ? (
        <img 
          src={icon} 
          alt="diet type icon" 
          style={{ 
            width: '40px', 
            height: '40px', 
            objectFit: 'cover', 
            borderRadius: '4px' 
          }} 
        />
      ) : (
        <span style={{ color: '#ccc' }}>—</span>
      ),
    },
    {
      title: t('Diet Type Name'),
      dataIndex: 'name',
      key: 'name',
      render: (name) => name?.[currentLanguage] || name?.['en'] || '',
    },
    {
      title: t('Number of Modes'),
      key: 'modesCount',
      width: 150,
      render: (_, record) => getDietModesForType(record.id).length,
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleUpdateType(record)}
            size="small"
          >
            {t('Edit')}
          </Button>
          <Popconfirm
            title={t('Are you sure you want to delete this diet type?')}
            onConfirm={() => handleDeleteType(record.id)}
            okText={t('Yes')}
            cancelText={t('No')}
          >
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              {t('Delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <section className={css.wrap}>
      <div className={css.header}>
        <h2>{t('Diet Types & Modes')}</h2>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreateType}
          size="large"
        >
          {t('Create New Diet Type')}
        </Button>
      </div>

      <div className={css.tableContainer}>
        <Table
          columns={typeColumns}
          dataSource={dietTypes}
          rowKey="id"
          loading={loading}
          expandable={{
            expandedRowRender,
            rowExpandable: (record) => true,
          }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `${t('Total')} ${total} ${t('items')}`,
          }}
          bordered
        />
      </div>

      {/* Diet Type Modals */}
      {isCreateTypeModalOpen && (
        <DietTypeCreateModal
          isOpen={isCreateTypeModalOpen}
          setIsOpen={setIsCreateTypeModalOpen}
        />
      )}

      {isUpdateTypeModalOpen && (
        <DietTypeUpdateModal
          isOpen={isUpdateTypeModalOpen}
          setIsOpen={setIsUpdateTypeModalOpen}
          dietType={selectedDietType}
        />
      )}

      {/* Diet Mode Modals */}
      {isCreateModeModalOpen && (
        <DietModeCreateModal
          isOpen={isCreateModeModalOpen}
          setIsOpen={setIsCreateModeModalOpen}
          dietTypeId={selectedDietTypeIdForMode}
        />
      )}

      {isUpdateModeModalOpen && (
        <DietModeUpdateModal
          isOpen={isUpdateModeModalOpen}
          setIsOpen={setIsUpdateModeModalOpen}
          dietMode={selectedDietMode}
        />
      )}
    </section>
  );
};

export default DietModeAndTypes;
